import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const VDMomentsEditor = () => {
  const LANGS = ["En", "Ar", "Ku"];

  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    // PARAGRAPHS
    Paragraph1En: "",
    Paragraph1Ar: "",
    Paragraph1Ku: "",

    Paragraph2En: "",
    Paragraph2Ar: "",
    Paragraph2Ku: "",

    // BUTTON
    ButtonEn: "",
    ButtonAr: "",
    ButtonKu: "",

    // SPECS
    Specs: [],

    // CARDS
    Cards: [],
  });

  const [images, setImages] = useState({
    bottom: null,
    cards: {},
  });

  // MODAL STATE
  const [showModal, setShowModal] = useState(false);
  const [uploadField, setUploadField] = useState(null); // "bottom" OR cardId
  const [uploadFile, setUploadFile] = useState(null);

  const resolveUrl = (u) =>
    u?.startsWith("http") ? u : `${API_BASE}${u}`;

  // LOAD ALL LANGUAGES
  useEffect(() => {
    const load = async () => {
      try {
        const [en, ar, ku] = await Promise.all([
          axios.get(`${API_BASE}/api/VDMomentsSection?lang=en`),
          axios.get(`${API_BASE}/api/VDMomentsSection?lang=ar`),
          axios.get(`${API_BASE}/api/VDMomentsSection?lang=ku`),
        ]);

        // Setup paragraphs + button
        const f = {
          Paragraph1En: en.data.paragraph1,
          Paragraph1Ar: ar.data.paragraph1,
          Paragraph1Ku: ku.data.paragraph1,

          Paragraph2En: en.data.paragraph2,
          Paragraph2Ar: ar.data.paragraph2,
          Paragraph2Ku: ku.data.paragraph2,

          ButtonEn: en.data.button,
          ButtonAr: ar.data.button,
          ButtonKu: ku.data.button,

          Specs: en.data.specs.map((s, i) => ({
            SpecId: i === 0 ? 17010 : i === 1 ? 17011 : i === 2 ? 17012 : 17013,
            ValueEn: s.value,
            ValueAr: ar.data.specs[i].value,
            ValueKu: ku.data.specs[i].value,

            LabelEn: s.label,
            LabelAr: ar.data.specs[i].label,
            LabelKu: ku.data.specs[i].label,
          })),

          Cards: en.data.cards.map((c, idx) => ({
            CardId: c.id,
            TitleEn: c.title,
            TitleAr: ar.data.cards[idx].title,
            TitleKu: ku.data.cards[idx].title,

            DescEn: c.desc,
            DescAr: ar.data.cards[idx].desc,
            DescKu: ku.data.cards[idx].desc,
          })),
        };

        // IMAGES
        setImages({
          bottom: en.data.bottomImage,
          cards: en.data.cards.reduce((acc, c) => {
            acc[c.id] = c.image;
            return acc;
          }, {}),
        });

        setForm(f);
        setLoading(false);
      } catch (err) {
        console.error("LOAD FAILED", err);
      }
    };

    load();
  }, []);

  // CHANGE HANDLER
  const handleChange = (e, idx, field, group = null) => {
    if (group === "specs") {
      const updated = [...form.Specs];
      updated[idx][field] = e.target.value;
      setForm({ ...form, Specs: updated });
      return;
    }

    if (group === "cards") {
      const updated = [...form.Cards];
      updated[idx][field] = e.target.value;
      setForm({ ...form, Cards: updated });
      return;
    }

    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // SAVE TEXT
  const handleSave = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.put(`${API_BASE}/api/VDMomentsSection`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Saved successfully!");
    } catch (err) {
      console.error("SAVE FAILED", err);
      alert("Saving failed.");
    }
  };

  // UPLOAD MEDIA
  const handleUpload = async () => {
    if (!uploadFile) return alert("Select a file first.");

    const fd = new FormData();
    const token = localStorage.getItem("token");

    if (uploadField === "bottom") {
      fd.append("BottomImage", uploadFile);
    } else {
      fd.append("CardId", uploadField);
      fd.append("CardImage", uploadFile);
    }

    try {
      await axios.post(`${API_BASE}/api/VDMomentsSection/media`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Uploaded!");
      window.location.reload();
    } catch (err) {
      console.error("UPLOAD FAILED", err);
    }
  };

  if (loading) return <p>Loading...</p>;

  // =============================
  // RENDERING
  // =============================
  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">VOYAH DREAM — Moments Section Editor</h2>

      <div className="row">
        {LANGS.map((lang) => (
          <div key={lang} className="col-lg-4 col-md-6 mb-4">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="text-primary">{lang}</h5>

                <label className="form-label mt-3">Paragraph 1</label>
                <textarea
                  className="form-control"
                  rows={2}
                  name={`Paragraph1${lang}`}
                  value={form[`Paragraph1${lang}`]}
                  onChange={handleChange}
                />

                <label className="form-label mt-3">Paragraph 2</label>
                <textarea
                  className="form-control"
                  rows={2}
                  name={`Paragraph2${lang}`}
                  value={form[`Paragraph2${lang}`]}
                  onChange={handleChange}
                />

                <label className="form-label mt-3">Button Text</label>
                <input
                  className="form-control"
                  name={`Button${lang}`}
                  value={form[`Button${lang}`]}
                  onChange={handleChange}
                />

                <h6 className="fw-bold mt-4">Specs</h6>

                {form.Specs.map((s, idx) => (
                  <div key={s.SpecId} className="mb-3">
                    <label className="form-label">
                      Spec {idx + 1} Value
                    </label>
                    <input
                      className="form-control"
                      value={s[`Value${lang}`]}
                      onChange={(e) =>
                        handleChange(e, idx, `Value${lang}`, "specs")
                      }
                    />

                    <label className="form-label mt-2">
                      Spec {idx + 1} Label
                    </label>
                    <input
                      className="form-control"
                      value={s[`Label${lang}`]}
                      onChange={(e) =>
                        handleChange(e, idx, `Label${lang}`, "specs")
                      }
                    />
                  </div>
                ))}

                <h6 className="fw-bold mt-4">Cards</h6>

                {form.Cards.map((c, idx) => (
                  <div key={c.CardId} className="mb-3">
                    <label className="form-label">
                      Card {idx + 1} Title
                    </label>
                    <input
                      className="form-control"
                      value={c[`Title${lang}`]}
                      onChange={(e) =>
                        handleChange(e, idx, `Title${lang}`, "cards")
                      }
                    />

                    <label className="form-label mt-2">
                      Card {idx + 1} Description
                    </label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={c[`Desc${lang}`]}
                      onChange={(e) =>
                        handleChange(e, idx, `Desc${lang}`, "cards")
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* SAVE BTN */}
      <div className="text-end mb-4">
        <button className="btn btn-primary px-4" onClick={handleSave}>
          Save Changes
        </button>
      </div>

      {/* BOTTOM IMAGE */}
      <h4>Bottom Image</h4>
      <div className="card shadow-sm mb-4">
        <div className="card-body">
          {images.bottom && (
            <img
              src={resolveUrl(images.bottom)}
              className="w-100 rounded mb-3"
              style={{ maxHeight: "250px", objectFit: "cover" }}
              alt=""
            />
          )}

          <button
            className="btn btn-primary"
            onClick={() => {
              setUploadField("bottom");
              setUploadFile(null);
              setShowModal(true);
            }}
          >
            Upload / Replace
          </button>
        </div>
      </div>

      {/* CARD IMAGES */}
      <h4>Card Images</h4>
      <div className="row">
        {form.Cards.map((c, idx) => (
          <div key={c.CardId} className="col-md-6 mb-4">
            <div className="card shadow-sm">
              <div className="card-body">
                <h6>Card {idx + 1}</h6>

                <img
                  src={resolveUrl(images.cards[c.CardId])}
                  className="w-100 rounded mb-2"
                  style={{ maxHeight: "200px", objectFit: "cover" }}
                  alt=""
                />

                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setUploadField(c.CardId);
                    setUploadFile(null);
                    setShowModal(true);
                  }}
                >
                  Upload / Replace
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* MODAL */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">Upload Image</h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-primary"
                    onClick={handleUpload}
                  >
                    Upload
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default VDMomentsEditor;
